import { useState } from 'react';
import { useAuth } from '@toki/auth';
import type { Requester } from '../services/api';
import { deleteRequester, sendRequesterAccess, updateRequester, UserHasHistoryError } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { PersonaModal } from './PersonaModal';
import styles from '../screens/Mantenimiento.module.css';

/** «Activo» con el cliente desactivado NO entra (paso 85a): es un tercer estado
 *  y se dice, o la pantalla mentiría justo en el caso que importa. */
function EstadoPersona({ p }: { p: Requester }) {
  if (!p.isActive) return <span className={styles.badgeInactive}>Inactivo</span>;
  if (!p.isLive) return <span className={styles.badgeWarn} title="Su cliente está desactivado: no puede entrar ni escribir por correo">Cliente desactivado</span>;
  return <span className={styles.badgeActive}>Activo</span>;
}

/**
 * Las personas de cliente, con sus acciones. La usan la pantalla Usuarios (todas,
 * con la columna Cliente) y la ficha de un cliente (las suyas, sin ella).
 *
 * Permisos (decisión de Jose, D1): el técnico corrige, desactiva y borra
 * personas (`users:manage_requesters`) y manda el acceso
 * (`users:create_requester`). Los botones que fallarían no se pintan; la
 * frontera la pone el backend igual.
 */
export function PersonasTabla({
  personas,
  showCliente,
  onChanged,
  onNotice,
}: {
  personas: Requester[];
  showCliente: boolean;
  onChanged: () => void | Promise<void>;
  onNotice: (msg: string) => void;
}) {
  const { provider } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const canManage = hasAnyPermission('users:manage', 'users:manage_requesters');
  const canSendAccess = hasAnyPermission('users:manage', 'users:create_requester', 'users:manage_requesters');

  const [editing, setEditing] = useState<Requester | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Requester | null>(null);
  // 3 = el backend dijo 409: la cuenta tiene historial y se ofrece desactivar (K41).
  const [deleteStep, setDeleteStep] = useState<1 | 2 | 3>(1);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  const withToken = async (fn: (token: string) => Promise<void>) => {
    const token = await provider.getAccessToken();
    if (!token) return;
    setError(null);
    try {
      await fn(token);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const toggleActive = (p: Requester) =>
    withToken(async (token) => {
      await updateRequester(p.id, { isActive: !p.isActive }, token);
      await onChanged();
    });

  const sendAccess = (p: Requester) =>
    withToken(async (token) => {
      await sendRequesterAccess(p.id, token);
      onNotice(`Enviado a ${p.email} su enlace de acceso al portal (vale 24 horas).`);
    });

  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteStep(1);
    setDeleteConfirmText('');
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const token = await provider.getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await deleteRequester(deleteTarget.id, token);
      closeDelete();
      await onChanged();
    } catch (e: any) {
      if (e instanceof UserHasHistoryError) setDeleteStep(3);
      else setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const deactivateInstead = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    await withToken(async (token) => {
      await updateRequester(deleteTarget.id, { isActive: false }, token);
      closeDelete();
      await onChanged();
    });
    setBusy(false);
  };

  const cols = showCliente ? 6 : 5;

  return (
    <>
      {error && !deleteTarget && <div className={styles.error} style={{ margin: '0 0 12px' }}>{error}</div>}
      <div className={styles.tableCard}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Email</th>
              {showCliente && <th>Cliente</th>}
              <th>Estado</th>
              <th>Alta</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {personas.map((p) => (
              <tr key={p.id} className={!p.isActive ? styles.inactive : ''}>
                <td>{p.displayName || '—'}</td>
                <td>{p.email}</td>
                {showCliente && (
                  <td>{p.clienteNombre ?? <span className={styles.muted}>Sin cliente</span>}</td>
                )}
                <td><EstadoPersona p={p} /></td>
                <td className={styles.num}>{new Date(p.createdAt).toLocaleDateString('es-ES')}</td>
                <td>
                  <div className={styles.actions}>
                  {canManage && <button className={styles.editBtn} onClick={() => setEditing(p)}>Editar</button>}
                  {canSendAccess && p.isLive && (
                    <button className={styles.editBtn} onClick={() => sendAccess(p)} title="Mandarle el enlace de entrada al portal">
                      Enviar acceso
                    </button>
                  )}
                  {canManage && (
                    <button
                      className={p.isActive ? styles.deactivateBtn : styles.editBtn}
                      onClick={() => toggleActive(p)}
                    >
                      {p.isActive ? 'Desactivar' : 'Reactivar'}
                    </button>
                  )}
                  {canManage && (
                    <button
                      className={styles.deactivateBtn}
                      onClick={() => { setDeleteTarget(p); setDeleteStep(1); setDeleteConfirmText(''); setError(null); }}
                    >
                      Borrar
                    </button>
                  )}
                  </div>
                </td>
              </tr>
            ))}
            {personas.length === 0 && (
              <tr><td colSpan={cols} className={styles.empty}>No hay personas</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <PersonaModal
          persona={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await onChanged(); }}
        />
      )}

      {deleteTarget && (
        <div className={styles.backdrop} onClick={closeDelete}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {deleteStep === 1 ? 'Borrar persona' : deleteStep === 2 ? 'Confirmar borrado' : 'No se puede borrar'}
              </h3>
              <button type="button" className={styles.modalClose} onClick={closeDelete} aria-label="Cerrar">&times;</button>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            {deleteStep === 1 ? (
              <>
                <div className={styles.modalBody}>
                  <p style={{ margin: 0 }}>
                    Vas a borrar la cuenta de <strong>{deleteTarget.displayName || deleteTarget.email}</strong>
                    {' '}(<code>{deleteTarget.email}</code>). Es <strong>irreversible</strong>.
                  </p>
                  <p className={styles.muted} style={{ margin: 0 }}>
                    Solo se puede borrar una cuenta que no ha llegado a usarse. Si tiene tickets o
                    actividad, no se borra: se desactiva y su historial se conserva.
                  </p>
                </div>
                <div className={styles.modalFooter}>
                  <button className={styles.cancelBtn} onClick={closeDelete}>Cancelar</button>
                  <button className={styles.deactivateBtn} onClick={() => setDeleteStep(2)}>Continuar</button>
                </div>
              </>
            ) : deleteStep === 3 ? (
              <>
                <div className={styles.modalBody}>
                  <p style={{ margin: 0 }}>
                    <strong>{deleteTarget.displayName || deleteTarget.email}</strong> tiene tickets o
                    actividad registrada, así que su cuenta no se puede borrar: se perdería el rastro de
                    lo que hizo.
                  </p>
                  <p style={{ margin: 0 }}>
                    {deleteTarget.isActive
                      ? 'Desactívala en su lugar: deja de poder entrar al portal y de escribir por correo, y sus tickets se conservan.'
                      : 'La cuenta ya está desactivada: no puede entrar al portal ni escribir por correo.'}
                  </p>
                </div>
                <div className={styles.modalFooter}>
                  <button className={styles.cancelBtn} onClick={closeDelete} disabled={busy}>Cerrar</button>
                  {deleteTarget.isActive && (
                    <button className={styles.deactivateBtn} onClick={deactivateInstead} disabled={busy}>
                      {busy ? 'Desactivando…' : 'Desactivar'}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className={styles.modalBody}>
                  <p style={{ margin: 0 }}>Para confirmar, escribe el email exacto:</p>
                  <code>{deleteTarget.email}</code>
                  <input
                    className={styles.input}
                    autoFocus
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder={deleteTarget.email}
                  />
                </div>
                <div className={styles.modalFooter}>
                  <button className={styles.cancelBtn} onClick={() => setDeleteStep(1)} disabled={busy}>Atrás</button>
                  <button
                    className={styles.deactivateBtn}
                    onClick={confirmDelete}
                    disabled={busy || deleteConfirmText.trim() !== deleteTarget.email}
                  >
                    {busy ? 'Borrando…' : 'Borrar definitivamente'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
